import { useCallback, useRef } from 'react';

import { ActionSubmit } from '@bonusly/common-action-input';
import { Avatar, Box, Button, CheckOutline, ChevronDownOutline, Flex, Popover, PopoverContent, PopoverTrigger, rem, Text } from '@bonusly/design-system';

import { useBudget } from '../hooks/use-budget';
import { UseProxyGivingResult } from '../hooks/use-proxy-giving';

// PROXY GIVING (POC)

interface ProxyGiveButtonProps {
	proxyGiving: UseProxyGivingResult;
}

export const ProxyGiveButton = ({ proxyGiving }: ProxyGiveButtonProps) => {
	const { principals, selectedPrincipal, selectPrincipal, clearSelection, hasProxyRights } = proxyGiving;
	const budget = useBudget();
	const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number, totalOptions: number, onSelect: () => void, onClose: VoidFunction) => {
		switch (e.key) {
			case 'Enter':
			case ' ':
				e.preventDefault();
				onSelect();
				onClose();
				break;
			case 'ArrowDown':
				e.preventDefault();
				if (index < totalOptions - 1) {
					optionRefs.current[index + 1]?.focus();
				}
				break;
			case 'ArrowUp':
				e.preventDefault();
				if (index > 0) {
					optionRefs.current[index - 1]?.focus();
				}
				break;
			case 'Escape':
				e.preventDefault();
				onClose();
				break;
		}
	}, []);

	// PROXY GIVING (POC): Users without proxy rights see the standard button
	if (!hasProxyRights) {
		return <ActionSubmit size="lg" w="full" />;
	}

	const caretLabel = selectedPrincipal ? `As ${selectedPrincipal.name}` : 'As me';

	return (
		<Flex w="full">
			{/* PROXY GIVING (POC): Left segment — standard submit with right border removed */}
			<ActionSubmit size="lg" flex={1} borderRightRadius={0} />

			{/* PROXY GIVING (POC): Right segment — principal selector */}
			<Popover isLazy placement="bottom-end">
				{({ onClose }) => (
					<>
						<PopoverTrigger>
							<Button size="lg" borderLeftRadius={0} borderLeft="1px solid" borderLeftColor="whiteAlpha.300" px={3} aria-label={`Giving as ${caretLabel}`}>
								<Flex alignItems="center" gap={1}>
									<Text fontSize="sm" fontWeight="medium">
										{caretLabel}
									</Text>
									<ChevronDownOutline size="xs" />
								</Flex>
							</Button>
						</PopoverTrigger>

						<PopoverContent boxShadow="xl" minW={rem(240)} w="auto" p={1} border="none">
							<Box as="ul" role="listbox" aria-label="Select who to give as">
								{/* "As myself" option */}
								<PrincipalOption
									label="As myself"
									isSelected={selectedPrincipal === null}
									index={0}
									optionRefs={optionRefs}
									onSelect={() => {
										clearSelection();
										onClose();
									}}
									onKeyDown={(e, idx) => handleKeyDown(e, idx, principals.length + 1, () => clearSelection(), onClose)}
								/>

								{/* One entry per principal */}
								{principals.map((principal, i) => (
									<PrincipalOption
										key={principal.id}
										label={`${principal.name} (${budget.format(principal.givingBalance)})`}
										profilePicUrl={principal.profilePicUrl}
										isSelected={selectedPrincipal?.id === principal.id}
										index={i + 1}
										optionRefs={optionRefs}
										onSelect={() => {
											selectPrincipal(principal);
											onClose();
										}}
										onKeyDown={(e, idx) => handleKeyDown(e, idx, principals.length + 1, () => selectPrincipal(principal), onClose)}
									/>
								))}
							</Box>
						</PopoverContent>
					</>
				)}
			</Popover>
		</Flex>
	);
};

// PROXY GIVING (POC): Individual option row in the principal selector
interface PrincipalOptionProps {
	label: string;
	profilePicUrl?: string;
	isSelected: boolean;
	index: number;
	optionRefs: React.MutableRefObject<(HTMLLIElement | null)[]>;
	onSelect: () => void;
	onKeyDown: (e: React.KeyboardEvent, index: number) => void;
}

const PrincipalOption = ({ label, profilePicUrl, isSelected, index, optionRefs, onSelect, onKeyDown }: PrincipalOptionProps) => (
	<Box
		as="li"
		role="option"
		aria-selected={isSelected}
		tabIndex={-1}
		cursor="pointer"
		listStyleType="none"
		py={2}
		px={3}
		borderRadius="md"
		bg={isSelected ? 'primary.bgAlpha' : 'transparent'}
		_hover={{ bg: isSelected ? 'primary.bgAlpha' : 'bg.subtle' }}
		_focus={{ bg: isSelected ? 'primary.bgAlpha' : 'bg.subtle', outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: '-2px' }}
		ref={(el: HTMLLIElement | null) => {
			optionRefs.current[index] = el;
		}}
		onMouseDown={(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onSelect();
		}}
		onKeyDown={(e: React.KeyboardEvent) => onKeyDown(e, index)}
	>
		<Flex justifyContent="space-between" alignItems="center">
			<Flex alignItems="center" gap={2}>
				{profilePicUrl && <Avatar size="sm" src={profilePicUrl} name={label} />}
				<Text fontSize="sm" fontWeight="medium" color={isSelected ? 'primary.fg' : 'text.default'}>
					{label}
				</Text>
			</Flex>
			{isSelected && <CheckOutline boxSize={4} color="primary.fg" flexShrink={0} />}
		</Flex>
	</Box>
);
